import { ApolloError } from '@apollo/client';
import { GridPaginationModel } from '@mui/x-data-grid-pro';
import dayjs from 'dayjs';
import { PartnershipType } from '../../../../../types';
import { AggregateHoldsV2ItemType, ClientType } from '../../../types';
import { AutocompleteSelectOption } from '../../grid-components/grid-types';
import { InsurersType } from '../bill-grid-components/bill-management-types';

export interface HoldTypeOption {
  value: string;
  label: string;
}

export interface AggregateHoldsFiltersProps {
  filterParams: AggregateHoldsGridFilterModelType;
  setFilterParams: React.Dispatch<
    React.SetStateAction<AggregateHoldsGridFilterModelType>
  >;
  setPaginationModel: React.Dispatch<React.SetStateAction<GridPaginationModel>>;
  clients: ClientType[];
  clientsError?: ApolloError;
  clientsLoading?: boolean;
  insurers: InsurersType[];
  insurersError?: ApolloError;
  insurersLoading?: boolean;
  partnerships: PartnershipType[];
  partnershipsError?: ApolloError;
  partnershipsLoading?: boolean;
}

export type AggregateHoldsV2GridItemType = Pick<
  AggregateHoldsV2ItemType,
  | '__typename'
  | 'contractId'
  | 'holds'
  | 'holdsCount'
  | 'heldAmount'
  | 'minFromDate'
  | 'maxFromDate'
  | 'maxLastSeenAt'
>;

export type AggregateHoldDataType = AggregateHoldsV2GridItemType & {
  id: number; // Add ID for DataGrid
  contractDateRange: string | null;
  client: {
    id: number;
    name: string;
  };
  insurer: {
    id: number;
    name: string;
  };
  partnership: {
    id: number;
    name: string;
  };
  lastSeen: string; // maps from maxLastSeenAt
  holdType: string; // maps from holds
  totalHolds: number; // maps from holdsCount
  totalImpact: number; // maps from heldAmount
};

export type AggregateHoldDataList = AggregateHoldDataType[];

export enum AggregateGridColumnHeaderNames {
  CLIENT = 'Client',
  INSURER = 'Insurer',
  PARTNERSHIP = 'Partnership',
  CONTRACT_DATE_RANGE = 'Contract Date Range',
  LAST_SEEN = 'Last Seen',
  HOLD_TYPE = 'Hold Types',
  TOTAL_HOLDS = 'Total Holds',
  TOTAL_IMPACT = 'Total $ Impact',
}

export enum AggregateGridColumnFields {
  CLIENT = 'client',
  INSURER = 'insurer',
  PARTNERSHIP = 'partnership',
  CONTRACT_DATE_RANGE = 'contractDateRange',
  LAST_SEEN = 'lastSeen',
  HOLD_TYPE = 'holdType',
  TOTAL_HOLDS = 'totalHolds',
  TOTAL_IMPACT = 'totalImpact',
  ACTIONS = 'actions',
}

export type AggregateHoldsApiDataType = {
  items: AggregateHoldsV2GridItemType[];
  total: number;
  page: number;
  size: number;
  pages: number;
};

export interface AggregateHoldsGridFilterModelType {
  holdTypes: string[];
  startDate: string | null;
  endDate: string | null;
  minTotalImpact: number | null;
  maxTotalImpact: number | null;
  minTotalHolds: number | null;
  maxTotalHolds: number | null;
  clientIds: number[];
  insurerIds: number[];
  partnershipIds: number[];
}

export const AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS = 90;
export const AGGREGATE_HOLDS_DEFAULT_PAGE_SIZE = 50;

export const DEFAULT_HOLD_TYPES = [
  'loss_of_coverage',
  'eligibility_service_error',
  'carrier_change',
  'client_change',
  'partnership_change',
];
export const defaultAggregateHoldsGridFilterModel: AggregateHoldsGridFilterModelType =
  {
    holdTypes: DEFAULT_HOLD_TYPES,
    startDate: dayjs()
      .subtract(AGGREGATE_HOLDS_DEFAULT_LOOKBACK_DAYS, 'day')
      .format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    minTotalImpact: null,
    maxTotalImpact: null,
    minTotalHolds: null,
    maxTotalHolds: null,
    clientIds: [],
    insurerIds: [],
    partnershipIds: [],
  };

export interface RawAggregateAdvancedFiltersValues {
  holdTypes: AutocompleteSelectOption[] | [];
  dateRange: (Date | null)[];
  totalImpactRange: [number | null, number | null];
  totalHoldsRange: [number | null, number | null];
  clients: ClientType[] | [];
  insurers: InsurersType[] | [];
  partnerships: PartnershipType[] | [];
}
